/**
 * Command-special tables — per-hero direction+SPECIAL command mappings.
 *
 * Each table key is a context string recognised by resolveCommandSpecial():
 *   'dash'   → DashState active + SPECIAL
 *   'air'    → airborne + SPECIAL
 *   'down'   → DOWN held + SPECIAL
 *   'back'   → opposite-to-facing held + SPECIAL
 *   'toward' → facing-direction held + SPECIAL
 *
 * Values are move IDs that the Player wiring dispatches via the appropriate
 * execution path (performMove for hitbox moves, performThrow for command grabs).
 *
 * RAY "TANK" MILLER (MY-2436):
 *   Tank Buster      — toward + SPECIAL  → launcher strike (performMove)
 *   Spinning Piledriver — dash + SPECIAL → command grab (performThrow path)
 */
import type { CommandTable } from '@/systems/CommandSpecial';
import type { ProjectileDef } from '@/systems/ProjectileSystem';

/** Ray's command-special table. */
export const RAY_COMMAND_TABLE: CommandTable = {
  toward: 'rayTankBuster',
  dash: 'raySpinningPiledriver',
} as const;

/** Move ID for Ray's Spinning Piledriver (command grab variant). */
export const RAY_PILEDRIVER_ID = 'raySpinningPiledriver';

/** Recovery frames applied on a Spinning Piledriver whiff (no target found). */
export const RAY_PILEDRIVER_WHIFF_RECOVERY = 24;

/** Recovery frames applied on a Spinning Piledriver successful grab. */
export const RAY_PILEDRIVER_HIT_RECOVERY = 30;

// ── ERROL "DREAD" CLARKE command-special table (MY-2438) ──────────────────────
//
//   Boundary Smash   — toward + SPECIAL → horizontal cricket-ball/bat shockwave
//                      projectile (spawned via ProjectileSystem); moderate damage
//                      + knockback, travels across the screen.
//   Crease Sweep     — down  + SPECIAL → 360° spinning-bat melee AoE that hits
//                      BOTH sides; one wide centred hitbox (or two symmetric);
//                      longer recovery; good crowd control.

/** Errol's command-special table. */
export const ERROL_COMMAND_TABLE: CommandTable = {
  toward: 'errolBoundarySmash',
  down: 'errolCreaseSweep',
} as const;

/** Move ID for Errol's Boundary Smash (projectile command special). */
export const ERROL_BOUNDARY_SMASH_ID = 'errolBoundarySmash';

/** Move ID for Errol's Crease Sweep (360° melee AoE command special). */
export const ERROL_CREASE_SWEEP_ID = 'errolCreaseSweep';

/** Recovery frames for Errol's Crease Sweep (longer — crowd control trade-off). */
export const ERROL_CREASE_SWEEP_RECOVERY = 36;

/**
 * Projectile definition for Errol's Boundary Smash shockwave.
 * Consumed by Player.ts when spawning via ProjectileManager.
 * Pure data — no Phaser at import time (ProjectileDef is a type-only import).
 */
export const ERROL_BOUNDARY_SMASH_PROJ_DEF: ProjectileDef = {
  speed: 6,
  maxRange: 400,
  w: 36,
  h: 40,
  spec: { damage: 14, knockback: 28, hitstun: 20, sfxTag: 'errolBoundarySmash' },
};

// ── KRIS "HAMMER" VANCE command-special table (MY-2418) ───────────────────────
//
//   Hammer Throw  — toward + SPECIAL → thrown projectile that optionally arcs
//                   back (boomerang-return flag); moderate damage + stun on hit.
//   Ground Zero   — down  + SPECIAL → overhead slam into a low ground-shockwave
//                   AoE; wide melee hitbox hitting enemies near the impact.

/** Kris's command-special table. */
export const KRIS_COMMAND_TABLE: CommandTable = {
  toward: 'krisHammerThrow',
  down: 'krisGroundZero',
} as const;

/** Move ID for Kris's Hammer Throw (projectile command special). */
export const KRIS_HAMMER_THROW_ID = 'krisHammerThrow';

/** Move ID for Kris's Ground Zero (overhead slam + shockwave AoE). */
export const KRIS_GROUND_ZERO_ID = 'krisGroundZero';

/** Recovery frames for Kris's Ground Zero (overhead slam commit). */
export const KRIS_GROUND_ZERO_RECOVERY = 38;

/**
 * Projectile definition for Kris's Hammer Throw.
 * boomerang: true signals ProjectileSystem (via optional flag) to return the
 * projectile toward the caster once it reaches half its maxRange.
 */
export const KRIS_HAMMER_THROW_PROJ_DEF: ProjectileDef = {
  speed: 5,
  maxRange: 360,
  w: 32,
  h: 32,
  boomerang: true,
  spec: { damage: 16, knockback: 24, hitstun: 22, sfxTag: 'krisHammerThrow' },
};

// ── PRIYA "JAGUAR" HASSAN command-special table (MY-2418) ─────────────────────
//
//   Tiger Knee    — toward + SPECIAL → rising knee strike; launcher.
//   Jaguar Dive   — air   + SPECIAL → downward dive-kick (only when airborne).
//   Rising Fury   — down  + SPECIAL → flash-kick anti-air launcher.

/** Priya's command-special table. */
export const PRIYA_COMMAND_TABLE: CommandTable = {
  toward: 'priyaTigerKnee',
  air: 'priyaJaguarDive',
  down: 'priyaRisingFury',
} as const;

/** Move ID for Priya's Tiger Knee (rising knee launcher). */
export const PRIYA_TIGER_KNEE_ID = 'priyaTigerKnee';

/** Move ID for Priya's Jaguar Dive (airborne dive-kick). */
export const PRIYA_JAGUAR_DIVE_ID = 'priyaJaguarDive';

/** Move ID for Priya's Rising Fury (flash-kick anti-air). */
export const PRIYA_RISING_FURY_ID = 'priyaRisingFury';

// ── GHOST "BLADE" CHEN command-special table (MY-2418) ────────────────────────
//
//   Blade Rekka   — toward + SPECIAL → advancing multi-hit knife flurry.
//   Shadow Step   — back   + SPECIAL → short backward teleport/evade with brief
//                   i-frames (reposition tool, low cost).
//   Flashbang     — down   + SPECIAL → projectile that stuns/hitstuns on hit.

/** Ghost's command-special table. */
export const GHOST_COMMAND_TABLE: CommandTable = {
  toward: 'ghostBladeRekka',
  back: 'ghostShadowStep',
  down: 'ghostFlashbang',
} as const;

/** Move ID for Ghost's Blade Rekka (multi-hit knife flurry). */
export const GHOST_BLADE_REKKA_ID = 'ghostBladeRekka';

/** Move ID for Ghost's Shadow Step (teleport evade). */
export const GHOST_SHADOW_STEP_ID = 'ghostShadowStep';

/** Move ID for Ghost's Flashbang (stun projectile). */
export const GHOST_FLASHBANG_ID = 'ghostFlashbang';

/** I-frame count applied during Shadow Step's evade window. */
export const GHOST_SHADOW_STEP_IFRAMES = 12;

/** Recovery frames for Ghost's Shadow Step (after reposition). */
export const GHOST_SHADOW_STEP_RECOVERY = 14;

/** Flashbang stun duration applied on hit (frames). */
export const GHOST_FLASHBANG_STUN = 30;

/**
 * Projectile definition for Ghost's Flashbang.
 * Short range; on hit it delivers hitstun = GHOST_FLASHBANG_STUN so the
 * opponent is locked long enough for a follow-up.
 */
export const GHOST_FLASHBANG_PROJ_DEF: ProjectileDef = {
  speed: 7,
  maxRange: 280,
  w: 28,
  h: 28,
  spec: { damage: 8, knockback: 10, hitstun: 30, sfxTag: 'ghostFlashbang' },
};

// ── THE PRAETOR command-special table (100% unlock) ──────────────────────────
//
//   Shield Bash  — toward + SPECIAL → armoured launcher shove.
//   Baton Shock  — down   + SPECIAL → close stun strike with high hitstun.

export const PRAETOR_COMMAND_TABLE: CommandTable = {
  toward: 'praetorShieldBash',
  down: 'praetorBatonShock',
} as const;

export const PRAETOR_SHIELD_BASH_ID = 'praetorShieldBash';
export const PRAETOR_BATON_SHOCK_ID = 'praetorBatonShock';

// ── THE RT HON. HALLOWAY command-special table (14/14 joke unlock) ───────────
//
//   Bribe Toss   — toward + SPECIAL → low-damage projectile with long stun.
//   Cower Dodge  — back   + SPECIAL → short panic retreat with i-frames.

export const HALLOWAY_COMMAND_TABLE: CommandTable = {
  toward: 'hallowayBribeToss',
  back: 'hallowayCowerDodge',
} as const;

export const HALLOWAY_BRIBE_TOSS_ID = 'hallowayBribeToss';
export const HALLOWAY_COWER_DODGE_ID = 'hallowayCowerDodge';

export const HALLOWAY_COWER_DODGE_IFRAMES = 18;
export const HALLOWAY_COWER_DODGE_RECOVERY = 20;

export const HALLOWAY_BRIBE_TOSS_PROJ_DEF: ProjectileDef = {
  speed: 5,
  maxRange: 240,
  w: 24,
  h: 24,
  spec: { damage: 1, knockback: 4, hitstun: 52, sfxTag: 'hallowayBribeToss' },
};
